import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const apiCall = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` }
});

const EMPTY_FORM = { name: '', description: '', distanceRequired: '', coins: '' };

const Rewards = () => {
  const { logActivity } = useActivity?.() || {};
  const [rewards, setRewards]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // null = create, object = edit
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(null);

  useEffect(() => { loadRewards(); }, []);

  const loadRewards = async () => {
    setLoading(true);
    try {
      const res = await apiCall().get('/api/admin/rewards');
      const list = res.data?.data || res.data || [];
      const sorted = [...(Array.isArray(list) ? list : [])].sort((a, b) => (a.distanceRequired ?? 0) - (b.distanceRequired ?? 0));
      setRewards(sorted);
    } catch {
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditTarget(r);
    setForm({ name: r.name || '', description: r.description || '', distanceRequired: r.distanceRequired ?? '', coins: r.coins ?? '' });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.distanceRequired === '' || isNaN(Number(form.distanceRequired))) { toast.error('Valid distance required'); return; }
    if (form.coins === '' || isNaN(Number(form.coins))) { toast.error('Valid coins value required'); return; }

    setSaving(true);
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      distanceRequired: Number(form.distanceRequired),
      coins: Number(form.coins),
    };

    try {
      if (editTarget) {
        await apiCall().put(`/api/admin/rewards/${editTarget.id}`, body);
        toast.success('Reward updated');
      } else {
        await apiCall().post('/api/admin/rewards', body);
        toast.success('Reward created');
      }
      closeModal();
      loadRewards();
    } catch {
      toast.error(editTarget ? 'Failed to update reward' : 'Failed to create reward');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reward? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await apiCall().delete(`/api/admin/rewards/${id}`);
      toast.success('Reward deleted');
      loadRewards();
    } catch {
      toast.error('Failed to delete reward');
    } finally {
      setDeleting(null);
    }
  };

  const totalCoins = rewards.reduce((sum, r) => sum + (r.coins ?? 0), 0);

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Game</span> / <span className="current">Rewards</span></div>
          <h1>🎁 Rewards</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Define distance milestones and coin rewards for walkers</p>
        </div>
        <button style={primaryBtnS} onClick={openCreate}>➕ Add Reward</button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '14px', marginBottom: '24px', maxWidth: '480px' }}>
        <div style={cardS}>
          <div style={{ fontSize: '1.5rem' }}>🎁</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--accent-primary)', marginTop: '6px' }}>{rewards.length}</div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '4px' }}>Total Rewards Defined</div>
        </div>
        <div style={cardS}>
          <div style={{ fontSize: '1.5rem' }}>🪙</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f59e0b', marginTop: '6px' }}>{totalCoins.toLocaleString()}</div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '4px' }}>Total Coins Defined</div>
        </div>
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading rewards…</div>
      ) : rewards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          No rewards defined yet. Click "Add Reward" to create one.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px' }}>
          {rewards.map(r => (
            <div key={r.id} style={{ ...cardS, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '2rem' }}>🎁</div>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>{r.name}</div>
              {r.description && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>{r.description}</div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                <span style={distBadgeS}>🏃 {r.distanceRequired ?? 0} km required</span>
                <span style={coinBadgeS}>🪙 {r.coins ?? 0} coins</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                <button style={editBtnS} onClick={() => openEdit(r)}>✏️ Edit</button>
                <button
                  style={{ ...deleteBtnS, opacity: deleting === r.id ? 0.6 : 1 }}
                  disabled={deleting === r.id}
                  onClick={() => handleDelete(r.id)}
                >
                  {deleting === r.id ? 'Deleting…' : '🗑️ Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div style={overlayS} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={modalS}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                {editTarget ? '✏️ Edit Reward' : '➕ Create Reward'}
              </h2>
              <button style={closeBtnS} onClick={closeModal}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelS}>Name *</label>
                <input
                  style={inputS}
                  placeholder="e.g. First 5km Achievement"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label style={labelS}>Description</label>
                <textarea
                  style={{ ...inputS, height: '80px', resize: 'vertical' }}
                  placeholder="Describe the reward…"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelS}>Distance Required (km) *</label>
                  <input
                    style={inputS}
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="5"
                    value={form.distanceRequired}
                    onChange={e => setForm(f => ({ ...f, distanceRequired: e.target.value }))}
                  />
                </div>
                <div>
                  <label style={labelS}>Coins Reward *</label>
                  <input
                    style={inputS}
                    type="number"
                    min="0"
                    placeholder="100"
                    value={form.coins}
                    onChange={e => setForm(f => ({ ...f, coins: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button style={cancelBtnS} onClick={closeModal}>Cancel</button>
              <button style={{ ...primaryBtnS, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : editTarget ? '💾 Save Changes' : '➕ Create Reward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', textAlign: 'center' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const editBtnS    = { flex: 1, padding: '7px 0', borderRadius: '7px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const deleteBtnS  = { flex: 1, padding: '7px 0', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(239,68,68,0.13)', color: '#ef4444' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const inputS      = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' };
const labelS      = { display: 'block', fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' };
const distBadgeS  = { padding: '3px 9px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(34,197,94,0.12)', color: '#22c55e' };
const coinBadgeS  = { padding: '3px 9px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(245,158,11,0.12)', color: '#f59e0b' };
const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' };
const closeBtnS   = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--text-muted)', padding: '2px 6px', borderRadius: '6px' };

export default Rewards;
